import AuthClient from '@skyscanner/auth-client';
import { GLOBAL } from 'saddlebag-browser';

import { isAcornRequest } from '../../helpers/isAcornRequest';
import { logError } from '../../helpers/logger';

import type { ContextLoginFormData } from '@web-platform/shared-universal-types/src/context';
import type { Nullable } from '@web-platform/shared-universal-types-utils/src/utils';

type AuthConsumer = 'banana' | 'acorn';
type GoogleOneTapAuthConsumer = 'google-yolo-banana';

let authClient: Nullable<any> = null;
let googleOneTapAuthClient: Nullable<any> = null;

const createAuthClient = (
  loginFormData: ContextLoginFormData,
  authConsumer: AuthConsumer | GoogleOneTapAuthConsumer,
  locationOrigin: string,
) => {
  if (loginFormData.isProductionEnv === false) {
    return new AuthClient(
      {
        authConsumer,
        env: 'development',
        locale: loginFormData.locale,
        market: loginFormData.market,
        strings: loginFormData.authClient.strings,
      },
      { authService: { baseUrl: `${locationOrigin}/g/traveller-auth-service` } },
    );
  }

  return new AuthClient({
    authConsumer,
    env: 'production',
    locale: loginFormData.locale,
    market: loginFormData.market,
    strings: loginFormData.authClient.strings,
  });
};

export const setAuthClient = (
  loginFormData: ContextLoginFormData,
  authConsumer: AuthConsumer,
  locationOrigin: string,
) => {
  try {
    authClient = createAuthClient(loginFormData, authConsumer, locationOrigin);
  } catch (error) {
    logError({
      error,
      component: 'AuthClient',
      message: 'Error setting instance of auth client',
    });
  }
};

export const setGoogleOneTapAuthClient = (
  loginFormData: ContextLoginFormData,
  authConsumer: GoogleOneTapAuthConsumer,
  locationOrigin: string,
) => {
  try {
    googleOneTapAuthClient = createAuthClient(loginFormData, authConsumer, locationOrigin);
  } catch (error) {
    logError({
      error,
      component: 'AuthClient',
      message: 'Error setting instance of Google One Tap auth client',
    });
  }
};

const getLoginFormDataFromWindow = (): Nullable<ContextLoginFormData> => {
  const $window = GLOBAL.getWindow();
  return $window.__internal?.loginFormComponentData ?? null;
};

export const getAuthClient = () => {
  if (!authClient) {
    const loginFormData = getLoginFormDataFromWindow();

    if (loginFormData) {
      setAuthClient(
        loginFormData,
        isAcornRequest() ? 'acorn' : 'banana',
        GLOBAL.getWindow().location.origin,
      );
    } else {
      logError({
        error: new Error('Auth client is not set.'),
        component: 'AuthClient',
        message: 'Error getting auth client',
      });
    }
  }
  return authClient;
};

// A separate instance is required as the Google One Tap global component matches its own
// login events by checking `authConsumer` contains 'google-yolo' - sharing the `authClient`
// instance above would cause it to ignore its own successful logins.
export const getGoogleOneTapAuthClient = () => {
  if (!googleOneTapAuthClient) {
    const loginFormData = getLoginFormDataFromWindow();

    if (loginFormData) {
      setGoogleOneTapAuthClient(
        loginFormData,
        'google-yolo-banana',
        GLOBAL.getWindow().location.origin,
      );
    } else {
      logError({
        error: new Error('Google One Tap auth client is not set.'),
        component: 'AuthClient',
        message: 'Error getting Google One Tap auth client',
      });
    }
  }
  return googleOneTapAuthClient;
};
